import { type ReactElement, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { requestBrowserPermissions } from '../../../helper/permissions';
import type { NowomedAndroidInterface } from '../../../hooks/useIsAndroidApp';
import { useIsAndroidApp } from '../../../hooks/useIsAndroidApp';
import UserService from '../../../services/User.service';
import { Button } from '../../atoms';
import { Icon } from '../../atoms/Icon/Icon';
import { TwilioRoom, TwilioRoomMessage } from '../../organisms/TwilioRoom/TwilioRoom';

/* eslint-disable no-unused-vars */
declare global {
  interface Window {
    NowomedAndroidInterface?: NowomedAndroidInterface;
    handleCameraPermission?: (granted: boolean) => void;
    handleAudioPermission?: (granted: boolean) => void;
  }
}
/* eslint-enable no-unused-vars */

const permissionDeniedMessage = 'Kamera- und Mikrofonberechtigungen sind erforderlich.';

export function AppointmentRoom(): ReactElement {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  if (typeof appointmentId !== 'string') {
    throw new Error('appointmentId param is must be string');
  }

  const navigate = useNavigate();

  const [name, setName] = useState<string>();
  const [token, setToken] = useState<string>();
  const [tokenFetchError, setTokenFetchError] = useState<string>('');
  const [mediaAccessError, setMediaAccessError] = useState<string>('');
  const [cameraPermissionStatus, setCameraPermissionStatus] = useState<'unknown' | 'granted' | 'denied'>('unknown');
  const [audioPermissionStatus, setAudioPermissionStatus] = useState<'unknown' | 'granted' | 'denied'>('unknown');

  const isAndroidApp = useIsAndroidApp();

  useEffect(() => {
    const cameraHandler = (granted: boolean): void => {
      setCameraPermissionStatus(granted ? 'granted' : 'denied');
    };
    const audioHandler = (granted: boolean): void => {
      setAudioPermissionStatus(granted ? 'granted' : 'denied');
    };
    window.handleCameraPermission = cameraHandler;
    window.handleAudioPermission = audioHandler;

    return () => {
      delete window.handleCameraPermission;
      delete window.handleAudioPermission;
    };
  }, []);

  useEffect(() => {
    const getToken = async () => {
      setTokenFetchError('');
      setMediaAccessError('');
      setCameraPermissionStatus('unknown');
      setAudioPermissionStatus('unknown');
      try {
        const { data } = await UserService.getTwilioPatientAppointmentToken(appointmentId);
        setToken(data.data.jwt);
        setName(data.data.room);
      } catch (err) {
        console.error('Error fetching Twilio token:', err);
        setTokenFetchError('Fehler beim Abrufen der Anrufinformationen.');
      }
    };
    getToken();
  }, [appointmentId]);

  useEffect(() => {
    const checkAndRequestPermissions = async () => {
      if (
        name &&
        token &&
        !tokenFetchError &&
        !mediaAccessError &&
        cameraPermissionStatus === 'unknown' &&
        audioPermissionStatus === 'unknown'
      ) {
        if (isAndroidApp) {
          window.NowomedAndroidInterface?.requestConferencePermissions();
        } else {
          const result = await requestBrowserPermissions();
          if (result.status === 'granted') {
            setCameraPermissionStatus('granted');
            setAudioPermissionStatus('granted');
          } else if (result.status === 'denied') {
            setCameraPermissionStatus('denied');
            setAudioPermissionStatus('denied');
          } else {
            setCameraPermissionStatus('denied');
            setAudioPermissionStatus('denied');
            setMediaAccessError(result.message);
          }
        }
      }
    };

    checkAndRequestPermissions();
  }, [name, token, tokenFetchError, mediaAccessError, cameraPermissionStatus, audioPermissionStatus, isAndroidApp]);

  const connectingMessage = 'Warten, bis das Zimmer bereit ist...';
  const waitingMessage = 'Ihr Arzt wird in wenigen Augenblicken hier sein...';

  let content: ReactElement;
  if (tokenFetchError) {
    content = <TwilioRoomMessage message={tokenFetchError} />;
  } else if (mediaAccessError) {
    content = <TwilioRoomMessage message={mediaAccessError} />;
  } else if (cameraPermissionStatus === 'denied' || audioPermissionStatus === 'denied') {
    content = <TwilioRoomMessage message={permissionDeniedMessage} />;
  } else if (name && token && cameraPermissionStatus === 'granted' && audioPermissionStatus === 'granted') {
    content = (
      <TwilioRoom name={name} token={token} waitingMessage={waitingMessage} connectingMessage={connectingMessage} />
    );
  } else {
    content = <TwilioRoomMessage message={connectingMessage} />;
  }

  return (
    <div
      style={{
        backgroundColor: '#111',
        color: 'white',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header
        style={{
          padding: '1rem',
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxSizing: 'border-box',
          flexShrink: 0,
        }}
      >
        <Icon icon='nowomed' size={150} color='white' />
        <Button onClick={() => navigate('/')} style={{ padding: '0.5rem 1rem' }}>
          Anruf beenden
        </Button>
      </header>
      <div
        style={{
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        {content}
      </div>
    </div>
  );
}
